import React, { ReactNode } from 'react'
import { keyedList } from './index'

export function formatNumber(
  num: number,
  decimal: number = 0,
  separator: number | null = 1000,
  trim: boolean = false
) {
  let str = typeof num === 'string' ? num : num.toFixed(decimal)
  if (trim) str = parseFloat(str).toString()
  if (separator == null) {
    return str
  }
  const parts = str.split('.')

  // TODO: Shouldn't we use the new str variable for comparison?
  const number = typeof num === 'string' ? parseFloat(str) : num
  if (number >= separator) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return parts.join('.')
}

export function formatDamage(val: number) {
  if (val > 1e13) {
    return formatNumber(val / 1e12, 3, 10000) + ' T'
  } else if (val > 1e10) {
    return formatNumber(val / 1e9, 3, 10000) + ' B'
  } else if (val > 1e7) {
    return formatNumber(val / 1e6, 3, 10000) + ' M'
  } else {
    return formatNumber(val, 0, 10000)
  }
}

type FormatCallback = (idx: number, fallback: string) => string
/**
 * Replaces {0} tags in string with provided arguments
 *
 * @param {string} str format string
 * @param {...string|FormatCallback} args substitutions or callback function
 * @returns {string} formatted string
 */
export function format(
  str: string,
  ...args: (string | number | FormatCallback)[]
) {
  const getter: FormatCallback =
    typeof args[0] === 'function'
      ? args[0]
      : (idx: number, fallback: string) =>
          idx in args ? (args[idx] as string) : fallback

  return str.replace(
    /{(\d+)(?::(.*?))?}(?:\|plural\((.*?)\))?/g,
    (m, idx, options, plural) => {
      const value = getter(parseInt(idx), m)
      if (plural) {
        const args = plural.split(',')
        return parseInt(value) > 1 ? args[1] : args[0]
      } else if (options) {
        return options.split('|')[parseInt(value) - 1]
      }
      return value
    }
  )
}

type FormatReactCallback = (idx: number) => string
/**
 * Replaces {0} tags in string with provided arguments, returns results as an array
 *
 * @param {string} str format string
 * @param {...ReactNode|FormatReactCallback} args substitutions or callback function
 * @returns {any[]} formatted string pieces
 */
export function formatReact(
  str: string,
  ...args: (ReactNode | FormatReactCallback)[]
) {
  const getter: FormatReactCallback =
    typeof args[0] === 'function'
      ? args[0]
      : (idx: number) => args[idx] as string

  return keyedList(
    str.split(/({\d+(?::.*?)?}(?:\|plural\(.*?\))?)/).map((piece) => {
      const m = piece.match(/^{(\d+)(?::(.*?))?}(?:\|plural\((.*)\))?$/)
      if (!m) return piece
      const value = getter(parseInt(m[1]))
      if (m[3]) {
        const args = m[3].split(',')
        return parseInt(value) > 1 ? args[1] : args[0]
      } else if (m[2]) {
        return m[2].split('|')[parseInt(value) - 1]
      }
      return value
    })
  )
}

export function _R(
  input: string | string[],
  ...args: (string | FormatReactCallback)[]
) {
  if (Array.isArray(input)) {
    const src = input
      .map(
        (str, index) =>
          (index ? `{${index - 1}}` : '') + str.replace(/[{}]/g, '\\$&')
      )
      .join('')
    return formatReact(src, ...args)
  }

  return input
}

type StringCallback = (text: string) => ReactNode
export function replaceReact(
  text: string,
  regex: RegExp,
  func: (...args: any[]) => ReactNode,
  textFunc?: StringCallback
) {
  const result = []
  function add(element: any) {
    if (React.isValidElement(element)) {
      result.push(React.cloneElement(element, { key: result.length }))
    } else if (element != null) {
      result.push(element)
    }
  }
  textFunc = textFunc || ((text) => text)
  let prev = 0,
    match
  while ((match = regex.exec(text))) {
    if (match.index > prev) {
      result.push(textFunc(text.substring(prev, match.index)))
    }
    const element = func(...match, match.index, match.input, match.groups)
    if (Array.isArray(element)) {
      for (const e of element) {
        add(e)
      }
    } else {
      add(element)
    }
    prev = match.index + match[0].length
    if (!regex.global && !regex.sticky) break
  }
  if (prev < text.length) {
    result.push(textFunc(text.substring(prev)))
  }
  return result
}

type HtmlTag = {
  tag: string
  attributes: Record<string, string>
  children: ReactNode[]
}

export function replaceHtml(
  text: string,
  Element: React.ComponentType<HtmlTag>,
  textFunc: StringCallback
) {
  const stack: HtmlTag[] = [
    {
      tag: 'TOP',
      attributes: {},
      children: []
    }
  ]
  function pop() {
    const top = stack.pop()!
    const elem = (
      <Element
        tag={top.tag}
        attributes={top.attributes}
        children={keyedList(top.children)}
      />
    )
    stack[stack.length - 1].children.push(elem)
  }
  for (const part of text.split(/(<[^>]*>)/)) {
    let m
    if ((m = part.match(/^<(\w+)(?:\s(.*)|=(.*))?(@.*)?>$/))) {
      const [_, tagName, attrs, tagValue, extra] = m
      const entry: HtmlTag = {
        tag: m[1].toLowerCase(),
        attributes: {},
        children: []
      }
      for (const [, key, value] of attrs?.matchAll(
        /(\w+)\s*=\s*['"](.*?)['"]/g
      ) ?? []) {
        entry.attributes[key.toLowerCase()] = value
      }
      if (tagValue) entry.attributes.argument = tagValue
      stack.push(entry)
      if (entry.tag === 'br' || extra) {
        pop()
      }
    } else if ((m = part.match(/^<\/(\w+)\s*>$/))) {
      const tag = m[1].toLowerCase()
      while (stack.length > 1 && stack[stack.length - 1].tag !== tag) {
        pop()
      }
      if (stack.length > 1) {
        pop()
      }
    } else if (part) {
      stack[stack.length - 1].children.push(textFunc ? textFunc(part) : part)
    }
  }
  while (stack.length > 1) {
    pop()
  }
  return keyedList(stack[0].children)
}

export function formatHtml(
  text: string,
  ...args: (string | number | FormatReactCallback)[]
) {
  if (!text) return null
  function replaceArgs(text: string) {
    return formatReact(text, ...args)
  }
  function replaceBreaks(text: string) {
    return replaceReact(
      text,
      /\n|&nbsp;/g,
      (txt: string) => {
        if (txt === '\n') return <br />
        if (txt === '&nbsp;') return '\u00a0'
      },
      replaceArgs
    )
  }

  text = text.replace(/{(\d+):(.*?)}/g, (m, idx, options) => {
    const value = args[Number(idx)]
    return options.split('|')[Number(value) - 1]
  })

  function TagElement({ tag, attributes, children }: HtmlTag) {
    if (tag === 'font') {
      const attr: Record<string, string> = {}
      //if (attributes.size) attr.fontSize = `${parseFloat(format(attributes.size, ...args)) / 14}em`;
      if (attributes.color) {
        const color = format(attributes.color, ...args)
        attr.color = color.replace(/#\w{2}(\w{6})/, (m, c) => `#${c}`)
      }
      return <span style={attr}>{children}</span>
    } else if (tag === 'br') {
      return <br />
    } else if (tag === 'textformat') {
      return <li>{children}</li>
    } else if (tag === 'color') {
      const attr: Record<string, string> = {}
      if (attributes.argument) {
        attr.color = attributes.argument
      }
      return <span style={attr}>{children}</span>
    } else {
      return <>{children}</>
    }
  }

  return replaceHtml(text, TagElement, replaceBreaks)
}
